/**
 * What this browser says about itself when it signs in (docs/signins.md
 * D502): its line on the account's list of sessions shows the browser and
 * the system — «Edge 141», «Windows». Read from the user agent and kept
 * coarse on purpose: a major version and a family name, nothing that tells
 * one machine from another the way a desktop's computer name does.
 */
export interface Device {
  name: string | null;
  system: string | null;
}

/** The browser, most specific first: Edge, Opera and Yandex say «Chrome»
 * too, and every Chromium says «Safari». */
const BROWSERS: [RegExp, string][] = [
  [/\bEdg(?:A|iOS)?\/(\d+)/, "Edge"],
  [/\bOPR\/(\d+)/, "Opera"],
  [/\bYaBrowser\/(\d+)/, "Yandex Browser"],
  [/\bFirefox\/(\d+)/, "Firefox"],
  [/\bFxiOS\/(\d+)/, "Firefox"],
  [/\bCriOS\/(\d+)/, "Chrome"],
  [/\bChrome\/(\d+)/, "Chrome"],
  [/\bVersion\/(\d+)[\d.]* (?:Mobile\/\S+ )?Safari\//, "Safari"],
];

/** The system, in the order that keeps a phone from reading as Linux or
 * macOS: Android says «Linux», and an iPad asking for desktop pages says
 * «Mac OS X». */
const SYSTEMS: [RegExp, string][] = [
  [/\bAndroid\b/, "Android"],
  [/\b(?:iPhone|iPod)\b/, "iOS"],
  [/\biPad\b/, "iPadOS"],
  [/\bWindows\b/, "Windows"],
  [/\bCrOS\b/, "ChromeOS"],
  [/\bMac OS X\b/, "macOS"],
  [/\bLinux\b/, "Linux"],
];

export function describeBrowser(ua: string): Device {
  let name: string | null = null;
  for (const [pattern, label] of BROWSERS) {
    const found = pattern.exec(ua);
    if (found) {
      name = `${label} ${found[1]}`;
      break;
    }
  }
  const system = SYSTEMS.find(([pattern]) => pattern.test(ua))?.[1] ?? null;
  return { name, system };
}

/** This browser, as the login body carries it. */
export function thisBrowser(): Device {
  return describeBrowser(typeof navigator === "undefined" ? "" : navigator.userAgent);
}

/** The words a device line needs: the unknown device. A slice of `Dict`, so
 * this file needs no dictionary of its own. */
export interface DeviceWords {
  sessionsUnknownDevice(): string;
}

/** A sign-in as a line, as the desktop's sessions sheet draws one
 * (docs/official-pet-plan.md D942, D987, signins.md D502; its `device_line`):
 * the name its device gave — «Неизвестное устройство» when it gave none —
 * then the build it signed in with — `desktop/0.13.40` is «EchoFloat 0.13.40»,
 * any `web/…` «EchoFloat Web», a product name the same in both languages,
 * anything else as it came — and the system, joined with « · ». What the
 * device said about itself, which could be a lie: the line names it and
 * claims nothing. */
export function deviceLine(
  signin: {
    device_name?: string | null;
    device_system?: string | null;
    client?: string | null;
  },
  t: DeviceWords,
): string {
  const client = (() => {
    const said = signin.client?.trim() ?? "";
    if (said === "") return null;
    const at = said.indexOf("/");
    const kind = at === -1 ? said : said.slice(0, at);
    if (kind === "desktop" && at !== -1) return `EchoFloat ${said.slice(at + 1)}`;
    if (kind === "web" && at !== -1) return "EchoFloat Web";
    return said;
  })();
  const name = signin.device_name?.trim() || t.sessionsUnknownDevice();
  const about = [client, signin.device_system]
    .map((part) => part?.trim() ?? "")
    .filter((part) => part !== "");
  return about.length > 0 ? `${name} · ${about.join(" · ")}` : name;
}

/** What the page can say about itself, as the report a piece of feedback
 * carries (official-pet-plan.md D947): the build and the device line the
 * login sends. The web keeps no log ring, so there is nothing more to say. */
export function pageReport(client: string, device: Device): string {
  return [
    `client: ${client}`,
    `browser: ${device.name ?? "unknown"}`,
    `system: ${device.system ?? "unknown"}`,
  ].join("\n");
}
